'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { BsFire, BsShare, BsDownload, BsCheck2 } from 'react-icons/bs'
import { StarRating } from '@/components/star-rating'
import { SuggestedEntities } from '@/components/suggested-entities'
import type { SuggestedEntity } from '@/lib/suggestions'
import type { StreakInfo } from '@/lib/streaks'
import type { CompletionRing } from '@/lib/completion'
import type { ShareTopRating } from '@/lib/rating-share'

export type SubmittedAttributeRating = {
  name: string
  value: number
}

interface RatingSuccessModalProps {
  open: boolean
  onClose: () => void
  entityId: string
  entitySlug: string
  entityName: string
  overallRating: number
  attributeRatings: SubmittedAttributeRating[]
  suggestions: SuggestedEntity[]
  isGuest?: boolean
  streak?: StreakInfo | null
  collection?: CompletionRing | null
  topOtherRatings?: ShareTopRating[]
}

function ProgressRing({
  percent,
  color,
  size = 72,
  stroke = 7,
}: {
  percent: number
  color: string
  size?: number
  stroke?: number
}) {
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (Math.min(100, Math.max(0, percent)) / 100) * circumference

  return (
    <svg width={size} height={size} className="block -rotate-90" aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-[stroke-dashoffset] duration-700 ease-out"
      />
    </svg>
  )
}

function shareUrlForEntity(slug: string) {
  if (typeof window === 'undefined') return `https://ratewdw.com/rate/${slug}`
  return `${window.location.origin}/rate/${slug}`
}

function shareFileName(entityName: string) {
  return `ratewdw-${entityName.replace(/[^\w]+/g, '-').toLowerCase() || 'rating'}.png`
}

async function blobFromShareApi(entityId: string, overallRating: number) {
  const res = await fetch('/api/ratings/share-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entityId, overallRating }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || 'Failed to create share image')
  }
  return res.blob()
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function supportsNativeShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

function canShareFiles(file: File) {
  if (!supportsNativeShare()) return false
  // Older browsers have share() but no canShare — try files anyway.
  if (typeof navigator.canShare !== 'function') return true
  try {
    return navigator.canShare({ files: [file] })
  } catch {
    return false
  }
}

function isShareAbort(err: unknown) {
  return err instanceof DOMException && err.name === 'AbortError'
}

export function RatingSuccessModal({
  open,
  onClose,
  entityId,
  entitySlug,
  entityName,
  overallRating,
  attributeRatings,
  suggestions,
  isGuest = false,
  streak = null,
  collection = null,
  topOtherRatings = [],
}: RatingSuccessModalProps) {
  const [sharing, setSharing] = useState(false)
  const [shareStatus, setShareStatus] = useState<string | null>(null)
  const [sharePrep, setSharePrep] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const shareFileRef = useRef<File | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  // Prefetch the PNG while the modal is open so Share runs inside the
  // user-gesture window (required for the native sheet on iOS/Android).
  useEffect(() => {
    if (!open) {
      setSharing(false)
      setShareStatus(null)
      setSharePrep('idle')
      shareFileRef.current = null
      return
    }

    let cancelled = false
    setSharePrep('loading')
    shareFileRef.current = null

    blobFromShareApi(entityId, overallRating)
      .then((blob) => {
        if (cancelled) return
        shareFileRef.current = new File([blob], shareFileName(entityName), {
          type: 'image/png',
        })
        setSharePrep('ready')
      })
      .catch((err: Error) => {
        if (cancelled) return
        shareFileRef.current = null
        setSharePrep('error')
        setShareStatus(err.message || 'Failed to prepare share image')
      })

    return () => {
      cancelled = true
    }
  }, [open, entityId, entityName, overallRating])

  if (!open) return null

  const handleShare = async () => {
    const url = shareUrlForEntity(entitySlug)
    const text = `I rated ${entityName} ${overallRating.toFixed(1)}/5 on RateWDW. Disagree? Share your rating at RateWDW.com`
    const title = `My rating for ${entityName}`
    const invite = `${text}\n${url}`
    const file = shareFileRef.current

    // Do NOT setState before navigator.share — React re-renders break the
    // user-activation token on iOS/Android and the sheet never opens.
    try {
      if (supportsNativeShare()) {
        if (file && canShareFiles(file)) {
          try {
            await navigator.share({ files: [file], title, text: invite })
            setShareStatus('Shared!')
            return
          } catch (err) {
            if (isShareAbort(err)) return
          }
        }

        try {
          await navigator.share({ title, text: invite, url })
          setShareStatus('Shared!')
          return
        } catch (err) {
          if (isShareAbort(err)) return
        }
      }

      setSharing(true)
      if (file) {
        downloadBlob(file, file.name)
        try {
          await navigator.clipboard.writeText(invite)
          setShareStatus('Image downloaded · link copied')
        } catch {
          setShareStatus('Image downloaded')
        }
        return
      }

      const blob = await blobFromShareApi(entityId, overallRating)
      const generated = new File([blob], shareFileName(entityName), { type: 'image/png' })
      shareFileRef.current = generated
      setSharePrep('ready')
      downloadBlob(generated, generated.name)
      setShareStatus('Image downloaded')
    } catch (err) {
      setShareStatus(err instanceof Error ? err.message : 'Share failed')
    } finally {
      setSharing(false)
    }
  }

  const shareDisabled = sharing || sharePrep === 'loading' || sharePrep === 'idle'

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rating-success-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div
          className="h-2.5 w-full"
          style={{
            background:
              'linear-gradient(90deg, #0f2654 0%, #1E4289 35%, #55B1F1 70%, #FEDB1A 100%)',
          }}
          aria-hidden="true"
        />
        <div className="p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#E79D20] mb-1">
            Rating saved
          </p>
          <h2 id="rating-success-title" className="text-xl font-bold text-[#1E4289] mb-4">
            Your rating for {entityName}
          </h2>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-sm font-semibold text-slate-700">Overall</span>
              <StarRating
                rating={overallRating}
                size="md"
                showNumber
                variant="user"
                orientation="horizontal"
              />
            </div>
            {attributeRatings.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-slate-200">
                {attributeRatings.map((ar) => (
                  <div key={ar.name} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-700 truncate">{ar.name}</span>
                    <StarRating
                      rating={ar.value}
                      size="sm"
                      showNumber
                      variant="user"
                      orientation="horizontal"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {!isGuest && (streak || collection) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {streak && (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex items-center gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FEDB1A] flex items-center justify-center">
                    <BsFire className="text-2xl text-[#1E4289]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                      Daily streak
                    </p>
                    <p className="text-2xl font-bold text-slate-800 leading-tight">
                      {streak.currentStreak}
                      <span className="text-sm font-medium text-slate-500 ml-1">
                        {streak.currentStreak === 1 ? 'day' : 'days'}
                      </span>
                    </p>
                    <p className="text-[11px] text-green-700 mt-0.5">
                      {streak.activeToday
                        ? 'Active today — streak safe'
                        : 'Keep going tomorrow'}
                    </p>
                    {streak.longestStreak > streak.currentStreak && (
                      <p className="text-[11px] text-slate-500">
                        Best: {streak.longestStreak} days
                      </p>
                    )}
                  </div>
                </div>
              )}

              {collection && collection.total > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <ProgressRing percent={collection.percent} color={collection.color} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-sm font-bold text-slate-800 leading-none">
                        {collection.percent}%
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Collection
                    </p>
                    <p className="text-sm font-bold text-slate-800 truncate">{collection.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {collection.rated} of {collection.total} rated
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {isGuest && (
            <div className="mb-5 rounded-xl border border-[#FEDB1A]/60 bg-[#FEDB1A]/15 p-4">
              <p className="text-sm font-semibold text-slate-800 mb-1">Keep rating</p>
              <p className="text-xs text-slate-600 mb-3">
                Create an account or log in to save this rating, build streaks, and track collection
                progress.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Link
                  href="/auth/signup"
                  className="flex-1 text-center px-4 py-2.5 bg-[#1E4289] text-white rounded-lg hover:bg-[#FEDB1A] hover:text-slate-900 font-bold text-sm transition-colors"
                >
                  Create an account
                </Link>
                <Link
                  href="/auth/signin"
                  className="flex-1 text-center px-4 py-2.5 border border-[#1E4289] text-[#1E4289] rounded-lg hover:bg-[#1E4289]/5 font-semibold text-sm transition-colors"
                >
                  Log in
                </Link>
              </div>
            </div>
          )}

          <div className="mb-5 rounded-xl border border-[#1E4289]/20 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-800 mb-1">Share your take</p>
            <p className="text-xs text-slate-600 mb-3">
              Post an image with this rating
              {topOtherRatings.length > 0
                ? ` and ${topOtherRatings.length} of your other top picks`
                : ''}
              . Friends land on the same rate page — invite them to disagree.
            </p>
            <button
              type="button"
              onClick={handleShare}
              disabled={shareDisabled && sharePrep !== 'error'}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#1E4289] text-white rounded-lg hover:bg-[#FEDB1A] hover:text-[#1E4289] disabled:opacity-60 font-bold text-sm transition-colors"
            >
              {sharePrep === 'loading' || sharePrep === 'idle' ? (
                <>
                  <BsDownload className="animate-pulse" />
                  Preparing image…
                </>
              ) : sharing ? (
                <>
                  <BsShare className="animate-pulse" />
                  Opening share…
                </>
              ) : shareStatus === 'Shared!' || shareStatus?.startsWith('Shared') ? (
                <>
                  <BsCheck2 />
                  Share again
                </>
              ) : (
                <>
                  <BsShare />
                  Share rating
                </>
              )}
            </button>
            {shareStatus && (
              <p className="text-xs text-center text-slate-600 mt-2">{shareStatus}</p>
            )}
            {sharePrep === 'error' && (
              <p className="text-xs text-center text-amber-700 mt-2">
                Image prep failed — tap Share to retry or download.
              </p>
            )}
          </div>

          {suggestions.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-bold text-slate-800 mb-1">Rate these next</h3>
              <p className="text-xs text-slate-500 mb-3">
                One pick from each park you haven&apos;t scored yet
              </p>
              <SuggestedEntities suggestions={suggestions} columns="1" compact />
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full mt-1 text-sm text-slate-500 hover:text-slate-700 py-2"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
